import { useContext } from 'react';
import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import './Footer.scss';
import { TaxonomyChild, TagWithMetadata } from 'app/api/tag-queries/types';
import { TagContext } from 'app/state/contexts/TagContext';
import { isFractionConceptType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { useTagging } from './useTagging';
import { FooterActionsMenu } from './FooterActionsMenu';
import { ScopeToggle } from './ScopeToggle';
import { ContinuationModal } from '../Modal/ContinuationModal';

interface TaxonomyFooterProps {
  selectedTag: TaxonomyChild | null;
  selectedLinkroleUri?: string | null;
  isContentMarked: boolean;
  isTagUsed?: boolean;
  documentTags: TagWithMetadata[];
  onExtensionClick?: (tagId: string) => void;
  onHiddenTagCreated?: (tag: unknown) => void;
}

export const TaxonomyFooter = ({
  selectedTag,
  selectedLinkroleUri,
  isContentMarked,
  isTagUsed,
  documentTags,
  onExtensionClick,
  onHiddenTagCreated,
}: TaxonomyFooterProps) => {
  const { blockSelection, blockScope, setBlockScope, tableSelection } = useContext(TagContext) ?? {};
  const {
    handleTagging,
    handleHiddenTagging,
    handleExcludeContent,
    handleCreateFootnote,
    isBlockTagging,
    hasContentMarked,
    showContinuationModal,
    handleContinuationConfirm,
    handleContinuationCancel,
  } = useTagging({
    selectedTag,
    selectedLinkroleUri,
    isContentMarked,
    documentTags,
  });

  const isTableScope = !!(tableSelection as any)?.element || blockSelection?.kind === 'table';
  const showScopeToggle = isTableScope;
  const effectiveScope = blockScope === 'table' ? 'table' : 'cell';

  const isTextBlockConcept = isEscapedConceptType((selectedTag as any)?.conceptType);
  const blockTagConceptMismatch = isBlockTagging && !!selectedTag && !isTextBlockConcept;
  const isFractionConcept = isFractionConceptType((selectedTag as any)?.conceptType);

  let description: string | null = null;

  if (blockTagConceptMismatch) {
    description = i18n('tag.footer.block.requires.textblock');
  } else if (hasContentMarked && selectedTag && !isTagUsed) {
    description = i18n('tag.footer.ready.to.use');
  } else if (hasContentMarked && selectedTag && isTagUsed) {
    description = i18n('tag.footer.already.used');
  } else if (hasContentMarked && !selectedTag) {
    description = i18n('tag.footer.missing.tag');
  } else if (!hasContentMarked && selectedTag) {
    description = i18n('tag.footer.no.content.marked');
  } else if (!hasContentMarked && !selectedTag) {
    description = i18n('tag.footer.select.tag.and.mark.content');
  }

  return (
    <footer className="tags-footer bg-white">
      {description && (
        <div className="tags-footer__info">
          <Typography variant="body-3" color="#717077" fontWeight="semibold">
            {i18n('tag.footer.type.taxonomy')}
          </Typography>

          <Typography variant="body-7" fontWeight="light">
            {description}
          </Typography>
        </div>
      )}

      {showScopeToggle && <ScopeToggle axis="table" scope={effectiveScope} onChange={(s) => setBlockScope?.(s)} />}

      <div className="tags-footer__actions">
        <button className="btn btn-secondary tags-footer__button" onClick={() => selectedTag && onExtensionClick?.(selectedTag.id)} disabled={!selectedTag}>
          {i18n('tag.extend.from.concept')}
        </button>
        <button
          className="btn btn-primary tags-footer__button"
          onClick={handleTagging}
          disabled={!selectedTag || !hasContentMarked || blockTagConceptMismatch || isFractionConcept}
        >
          {i18n('tag.use.tag')}
        </button>
        <FooterActionsMenu
          actions={[
            {
              key: 'hidden',
              label: i18n(isFractionConcept ? 'tag.footer.tag-as-fraction' : 'tag.hidden.tag-as-hidden'),
              disabled: !selectedTag || selectedTag.isAbstract || hasContentMarked,
              onClick: () => handleHiddenTagging(onHiddenTagCreated),
            },
            {
              key: 'exclude',
              label: i18n('tag.exclude.create'),
              // Excludes wrap a text selection — concept-independent.
              disabled: !isContentMarked,
              onClick: handleExcludeContent,
            },
            {
              key: 'footnote',
              label: i18n('tag.footnote.create'),
              disabled: !isContentMarked,
              onClick: handleCreateFootnote,
            },
          ]}
        />
      </div>

      <ContinuationModal show={showContinuationModal} onConfirm={handleContinuationConfirm} onCancel={handleContinuationCancel} />
    </footer>
  );
};
